import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [txn, setTxn] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setTxn(data);
        if (data.payment_status === "paid") { setStatus("success"); refresh(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts++ < 6) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch (e) {
        console.error("[payment-success] poll failed:", e?.response?.status || e?.message);
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div data-testid="payment-success" className="max-w-xl mx-auto px-6 sm:px-8 py-20 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[#1E3A8A] animate-spin" />
          <h1 className="font-heading text-2xl font-bold mt-6 text-[#0A192F]">Verifying payment…</h1>
          <p className="text-[#4A5568] mt-2">Please wait while we confirm with Stripe.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#276749]" />
          <h1 className="font-heading text-3xl font-bold mt-6 text-[#0A192F]">Payment successful</h1>
          <p className="text-[#4A5568] mt-3">{txn?.plan_name} is now active.</p>
          <Link to="/dashboard"><Button data-testid="back-to-dashboard" className="mt-8 bg-[#1E3A8A] hover:bg-[#1A365D] text-white">Go to Dashboard</Button></Link>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-[#E53E3E]" />
          <h1 className="font-heading text-3xl font-bold mt-6 text-[#0A192F]">Payment {status}</h1>
          <p className="text-[#4A5568] mt-3">{status === "timeout" ? "We couldn't confirm in time — please check back shortly." : "Something went wrong with your payment."}</p>
          <Link to="/pricing"><Button variant="outline" className="mt-8">Back to Pricing</Button></Link>
        </>
      )}
    </div>
  );
}
